import { useState, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Two-stage responsive toolbar detection.
 *
 * Stage 0  →  everything at full size
 * Stage 1  →  location selector compacts to icon-only, tabs start scrolling
 * Stage 2  →  search also collapses to icon button (last resort)
 *
 * Uses children-width measurement instead of scrollWidth so the container
 * does NOT need `overflow: hidden` — badges and other absolutely-positioned
 * elements stay visible.
 */

function measureChildrenWidth(el: HTMLElement): number {
    let total = 0;
    for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i] as HTMLElement;
        const style = getComputedStyle(child);
        total +=
            child.offsetWidth +
            parseFloat(style.marginLeft) +
            parseFloat(style.marginRight);
    }
    return total;
}

export function useResponsiveToolbar() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [stage, setStage] = useState<0 | 1 | 2>(0);

    const stageRef = useRef<0 | 1 | 2>(0);
    const fullWidthRef = useRef(0);
    const stage1WidthRef = useRef(0);

    stageRef.current = stage;

    // Synchronous check before paint — cascades stages without flicker
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        const cw = el.clientWidth;
        const childrenW = measureChildrenWidth(el);

        if (stageRef.current === 0 && childrenW > cw + 2) {
            fullWidthRef.current = childrenW;
            stageRef.current = 1;
            setStage(1);
        } else if (stageRef.current === 1 && childrenW > cw + 2) {
            stage1WidthRef.current = childrenW;
            stageRef.current = 2;
            setStage(2);
        }
    }, [stage]);

    // ResizeObserver for ongoing width changes
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        let raf = 0;

        const check = () => {
            const cw = el.clientWidth;
            const childrenW = measureChildrenWidth(el);
            const s = stageRef.current;

            if (s === 0 && childrenW > cw + 2) {
                fullWidthRef.current = childrenW;
                stageRef.current = 1;
                setStage(1);
            } else if (s === 1 && childrenW > cw + 2) {
                stage1WidthRef.current = childrenW;
                stageRef.current = 2;
                setStage(2);
            } else if (s > 0 && fullWidthRef.current > 0 && cw >= fullWidthRef.current + 24) {
                stageRef.current = 0;
                fullWidthRef.current = 0;
                stage1WidthRef.current = 0;
                setStage(0);
            } else if (s === 2 && stage1WidthRef.current > 0 && cw >= stage1WidthRef.current + 24) {
                stageRef.current = 1;
                stage1WidthRef.current = 0;
                setStage(1);
            }
        };

        const onResize = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(check);
        };

        const observer = new ResizeObserver(onResize);
        observer.observe(el);

        return () => {
            observer.disconnect();
            cancelAnimationFrame(raf);
        };
    }, []);

    return {
        containerRef,
        locationCompact: stage >= 1,
        searchCompact: stage >= 2,
    };
}
